import * as React from 'react';
import { Box, IconButton, Modal, Paper, Typography } from '@mui/material';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import { CloseRounded } from '@mui/icons-material';

type InfoBubbleProps = {
  title: string;
  children: React.ReactNode;
  fontColor?: string;
  backgroundColor?: string;
  iconAriaLabel?: string;
  maxWidth?: number;
  sideMargin?: number;
};

export const InfoBubble: React.FC<InfoBubbleProps> = ({
  title,
  children,
  fontColor = '#FFFFFF',
  backgroundColor = '#41797C',
  iconAriaLabel = 'More info',
  maxWidth = 340,
  sideMargin = 50,
}) => {
  const [open, setOpen] = React.useState(false);
  const handleToggle = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setOpen(!open);
  };
  const handleClose = () => setOpen(false);

  return (
    <Box component="span" sx={{ ml: 0, display: 'inline-flex', alignItems: 'center' }}>
      <IconButton size="small" aria-label={iconAriaLabel} onClick={handleToggle}>
        <InfoOutlined fontSize="inherit" />
      </IconButton>

      <Modal
        open={open}
        onClose={handleClose}
        slotProps={{ backdrop: { sx: { backgroundColor: 'rgba(0,0,0,0.5)' } } }} // dim rest of screen
        keepMounted
      >
        <Box
          sx={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: `min(${maxWidth}px, calc(100vw - ${sideMargin * 2}px))`, // Lesser value of maxWidth or 100vw - our margins
            maxHeight: 'calc(100vh - 40px)',
            outline: 0,
          }}
        >
          <Paper
            elevation={6}
            sx={{
              bgcolor: backgroundColor,
              color: 'common.white',
              borderRadius: 2,
              p: 2.5,
              overflowY: 'auto',
              position: 'relative',
            }}
            role="dialog"
          >
            <IconButton
              aria-label="Close"
              onClick={handleClose}
              sx={{ position: 'absolute', right: 6, top: 6, color: fontColor }}
            >
              <CloseRounded fontSize="medium" />
            </IconButton>

            <Typography
              variant="h6"
              sx={{
                fontFamily: 'Roboto',
                fontSize: '20px',
                fontWeight: 600,
                color: fontColor,
                textAlign: 'center',
              }}
            >
              {title}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontFamily: 'Roboto',
                fontSize: '16px',
                fontWeight: 400,
                color: fontColor,
                textAlign: 'center',
                marginTop: '5px',
              }}
            >
              {children}
            </Typography>
          </Paper>
        </Box>
      </Modal>
    </Box>
  );
};
